import { Avatar, Box, Typography } from '@cambridgeassessment/cambridge-ui';
import { Link as LinkDS } from '@cambridgeassessment/cambridge-ui/build/components/Link';
import { BusinessStreams } from 'ciam-commons';
import React, { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import OrgUserStatus from 'src/components/OrgUserStatus/OrgUserStatus';
import useAuth from 'src/hooks/useAuth';
import useOrg from 'src/hooks/useOrg';
import useOrgUsers from 'src/hooks/useOrgUsers';
import { OrgUser, RoleInfo } from 'src/types';
import { filterOrgUsers } from 'src/utils/filterOrgUsers';
import { transformRoles } from 'src/utils/transformers';
import OverflowTooltip from '../OverflowTooltip/OverflowTooltip';
import { routes } from '../Pages';
import Table from '../Table/Table';
import UserStatusDescriptionCard from '../TooltipDescriptionCard/UserStatusDescriptionCard';
import { headCells } from './constants';
import { useUsersTableStyles } from './styles';
import { Columns, Row } from './types';
import { getFullName, sortColumns } from './utils';

const tooltips = {
  status: <UserStatusDescriptionCard />,
};

export const createRows = (
  users: OrgUser[] = [],
  availableRoles: RoleInfo[] = [],
  isCambridgeAssessor?: boolean
): Row[] =>
  users.map((user) => {
    const { businessStream } = user;
    businessStream?.sort((a, b) => BusinessStreams[a] - BusinessStreams[b]);

    return {
      cells: {
        [Columns.FullName]: (
          <Box
            alignItems="center"
            display="flex"
            data-sortvalue={getFullName(user)}
          >
            <Box mr={2}>
              <Avatar
                src={user.profilePicture}
                data-testid="avatar-picture"
                size="xs"
              />
            </Box>
            <Typography variant="body1" data-testid="userName-typography">
              {getFullName(user)}
            </Typography>
          </Box>
        ),
        [Columns.Email]: (
          <Box data-sortvalue={user.email}>
            <OverflowTooltip variant="body1" data-testid="user-email">
              {user.email}
            </OverflowTooltip>
          </Box>
        ),
        [Columns.Roles]: (
          <Box minWidth={150}>
            <OverflowTooltip variant="body1" data-testid="roles">
              {transformRoles(user.roles, availableRoles)}
            </OverflowTooltip>
          </Box>
        ),
        ...(isCambridgeAssessor && {
          [Columns.CreditorNumber]: (
            <Box minWidth={120}>
              <OverflowTooltip>
                {user.creditorNumber ? user.creditorNumber : '-'}
              </OverflowTooltip>
            </Box>
          ),
          [Columns.BusinessUnit]: (
            <Box minWidth={120}>
              <OverflowTooltip>
                {businessStream?.join(', ') ?? '-'}
              </OverflowTooltip>
            </Box>
          ),
        }),
        [Columns.Status]: (
          <OrgUserStatus status={user.status} data-sortvalue={user.status} />
        ),
        [Columns.Link]: (
          <Typography variant="body1">
            <LinkDS
              component={Link}
              to={`${routes.MyOrganisation.path}/users/${user.UID}`}
              underline="none"
              tabIndex={0}
            >
              View
            </LinkDS>
          </Typography>
        ),
      },
      link: `${routes.MyOrganisation.path}/users/${user.UID}`,
    };
  });

const UsersTable: React.FC = () => {
  const {
    isLoading,
    users,
    error,
    userTableSettings,
    searchQuery,
    updateUserTableSettings,
  } = useOrgUsers();
  const {
    user: { isCambridgeAssessor },
  } = useAuth();

  const { orgRoles } = useOrg();
  const orgUsers = filterOrgUsers(searchQuery, users);

  const rows = useMemo(
    () => createRows(orgUsers, orgRoles.roles, isCambridgeAssessor),
    [orgUsers, orgRoles.roles, isCambridgeAssessor]
  );

  const classes = useUsersTableStyles({ isCAS: isCambridgeAssessor });
  const noResultsMessage = useMemo(
    () => (
      <Box>
        <Typography variant="body1">
          <Box display="flex" justifyContent="center">
            No results were found for&nbsp;
            <Typography variant="subtitle1">{searchQuery}</Typography>.
          </Box>
        </Typography>
        <Typography variant="body1">
          Check your spelling or try searching with another keyword.
        </Typography>
      </Box>
    ),
    [searchQuery]
  );

  return (
    <Table
      loading={isLoading}
      error={!!error}
      noResultsMessage={noResultsMessage}
      headCells={
        isCambridgeAssessor
          ? headCells
          : headCells.filter((el) => {
              return (
                [Columns.CreditorNumber, Columns.BusinessUnit].indexOf(el.id) <
                0
              );
            })
      }
      rows={rows}
      tooltips={tooltips}
      sortColumns={sortColumns}
      settings={userTableSettings}
      updateSettings={updateUserTableSettings}
      data-testid="usersTable"
      className={classes.table}
    />
  );
};

export default memo(UsersTable);
